import { useEffect, useRef, type RefObject } from 'react';
import type { SeekFn } from '@/types/required-video';

type Args = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onReady: (seek: SeekFn) => void;
  onTime: (t: number, d: number) => void;
};

const ORIGIN = 'https://www.loom.com';
const VERSION = '0.0.11';

type LoomMsg = {
  context: string;
  event?: string;
  value?: { seconds?: number; duration?: number };
};

function postCmd(
  iframe: HTMLIFrameElement | null,
  method: string,
  value?: string | number
): void {
  iframe?.contentWindow?.postMessage(
    JSON.stringify({
      context: 'player.js',
      version: VERSION,
      method,
      ...(value !== undefined ? { value } : {}),
    }),
    ORIGIN
  );
}

function parseMsg(data: unknown): LoomMsg | null {
  if (typeof data !== 'string') return null;
  try {
    const m = JSON.parse(data) as LoomMsg;
    return m?.context === 'player.js' ? m : null;
  } catch {
    return null;
  }
}

function buildSeek(iframe: HTMLIFrameElement | null): SeekFn {
  return (s) => postCmd(iframe, 'setCurrentTime', s);
}

function makeListener(args: {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onReadyRef: { current: (s: SeekFn) => void };
  onTimeRef: { current: (t: number, d: number) => void };
}): (e: globalThis.MessageEvent) => void {
  return (e) => {
    if (e.origin !== ORIGIN) return;
    const m = parseMsg(e.data);
    if (!m) return;
    if (m.event === 'ready') {
      const iframe = args.iframeRef.current;
      ['play', 'pause', 'ended', 'timeupdate'].forEach((ev) =>
        postCmd(iframe, 'addEventListener', ev)
      );
      args.onReadyRef.current(buildSeek(iframe));
    } else if (m.event === 'timeupdate') {
      const t = m.value?.seconds;
      const d = m.value?.duration;
      if (t != null && d) args.onTimeRef.current(t, d);
    }
  };
}

export function useLoomAdapter(args: Args): void {
  const onReadyRef = useRef(args.onReady);
  const onTimeRef = useRef(args.onTime);
  useEffect(() => {
    onReadyRef.current = args.onReady;
    onTimeRef.current = args.onTime;
  }, [args.onReady, args.onTime]);

  useEffect(() => {
    const onMsg = makeListener({
      iframeRef: args.iframeRef,
      onReadyRef,
      onTimeRef,
    });
    window.addEventListener('message', onMsg);
    return (): void => window.removeEventListener('message', onMsg);
  }, [args.iframeRef]);
}
